import { encryptStorage } from './storage';

export interface IOrganizationData {
  organizationId: string;
  organizationCode: string;
  type: 'SUPPLIER' | 'CONSUMER' | 'ON_ARRIVAL' | 'ONARRIVAL';
}

/**
 * Sets both organization ID and organization data in encrypted storage.
 * This ensures they are always synchronized.
 *
 * @param organizationId - The organization ID to store
 * @param organizationData - The organization data object containing code and type
 */
export function setOrganizationData(
  organizationId: string,
  organizationData: { organizationCode: string; type: string }
): void {
  try {
    encryptStorage.setItem('oa_organization_data', {
      organizationId: organizationId,
      organizationCode: organizationData.organizationCode,
      type: organizationData.type
    });
  } catch (error) {
    console.error('Error setting organization data:', error);
  }
}

/**
 * Gets organization data from encrypted storage.
 * Returns null if not found or on error.
 *
 * @returns The organization data object or null
 */
export function getOrganizationData(): IOrganizationData | null {
  try {
    const orgData = encryptStorage.getItem('oa_organization_data');
    if (orgData && typeof orgData === 'object') {
      return orgData as IOrganizationData;
    }
    return null;
  } catch (error) {
    console.error('Error retrieving organization data:', error);
    return null;
  }
}

/**
 * Gets organization ID from encrypted storage.
 * Returns empty string if not found.
 *
 * @returns The organization ID or empty string
 */
export function getOrganizationId(): string {
  try {
    const orgData = getOrganizationData();
    return orgData?.organizationId || '';
  } catch (error) {
    console.error('Error retrieving organization ID:', error);
    return '';
  }
}

/**
 * Clears organization data from encrypted storage.
 * Useful during logout.
 */
export function clearOrganizationData(): void {
  try {
    encryptStorage.removeItem('oa_organization_data');
  } catch (error) {
    console.error('Error clearing organization data:', error);
  }
}